import React, { FC, Suspense, useCallback, useEffect } from 'react';
import { T } from 'src/shared/helpers';
import AngleLeftIcon from 'src/shared/icons/angle-left-solid.svg';
import AngleRightIcon from 'src/shared/icons/angle-right-solid.svg';
import DownloadIcon from 'src/shared/icons/download-solid.svg';
import TimesIcon from 'src/shared/icons/times-solid.svg';
import styled, { keyframes } from 'styled-components';
import { Icon } from '../../../../widgets/icon';
import { LocalModel3DItem } from '../../assetLibrary/assetItem';
import { downloadFile } from '../../utils/downloadHelpers';
import { Viewport3D } from '../navigation/text3DWorkspaceView';

const GlbViewer = React.lazy(() => import('../shared/glbViewer'));

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.78);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${fadeIn} 0.18s ease;
`;

const DialogBox = styled.div`
    position: relative;
    width: 1344px;
    max-width: calc(100vw - 40px);
    height: 756px;
    max-height: calc(100vh - 40px);
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
`;

const TopBar = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 5;
`;

const IconButton = styled.button`
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background 0.15s ease,
        transform 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.75);
        transform: scale(1.05);
    }

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

const PreviewArea = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%);
    min-height: 0;
    overflow: hidden;
    position: relative;
`;

const FooterBar = styled.div`
    flex-shrink: 0;
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
`;

const ModelName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #1e0a3c;
`;

const NavArrow = styled.button<{ $side: 'left' | 'right' }>`
    position: absolute;
    top: 50%;
    ${(p) => (p.$side === 'left' ? 'left: 16px;' : 'right: 16px;')}
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
    transition:
        background 0.15s ease,
        transform 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.75);
        transform: translateY(-50%) scale(1.05);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

interface Local3DPreviewDialogProps {
    items: LocalModel3DItem[];
    index: number;
    onClose: () => void;
    onIndexChange: (index: number) => void;
}

export const Local3DPreviewDialog: FC<Local3DPreviewDialogProps> = ({ items, index, onClose, onIndexChange }) => {
    const item = items[index];

    const handlePrev = useCallback(() => {
        if (index > 0) onIndexChange(index - 1);
    }, [index, onIndexChange]);

    const handleNext = useCallback(() => {
        if (index < items.length - 1) onIndexChange(index + 1);
    }, [index, items.length, onIndexChange]);

    const handleDownload = useCallback(async () => {
        if (!item) return;
        try {
            if (item.modelUrl) {
                await downloadFile(item.modelUrl, `ai-3d-model-${item.label}.glb`);
            } else if (item.thumbnailUrl) {
                await downloadFile(item.thumbnailUrl, `ai-3d-model-${item.label}.png`);
            }
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [item]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') handlePrev();
            else if (e.key === 'ArrowRight') handleNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, handlePrev, handleNext]);

    if (!item) return null;

    return (
        <Backdrop onClick={onClose}>
            <DialogBox onClick={(e) => e.stopPropagation()}>
                <TopBar>
                    <IconButton onClick={handleDownload} title={T._('Download', 'Admin')}>
                        <Icon>
                            <DownloadIcon />
                        </Icon>
                    </IconButton>
                    <IconButton onClick={onClose} title={T._('Close', 'Admin')}>
                        <Icon>
                            <TimesIcon />
                        </Icon>
                    </IconButton>
                </TopBar>

                <PreviewArea>
                    {item.modelUrl ? (
                        <Suspense fallback={null}>
                            <GlbViewer url={item.modelUrl} />
                        </Suspense>
                    ) : (
                        <Viewport3D key={item.id} modelGenerated agentType={item.toolId} />
                    )}
                </PreviewArea>

                {items.length > 1 && (
                    <>
                        <NavArrow $side='left' onClick={handlePrev} disabled={index === 0} title={T._('Previous', 'Admin')}>
                            <Icon>
                                <AngleLeftIcon />
                            </Icon>
                        </NavArrow>
                        <NavArrow $side='right' onClick={handleNext} disabled={index === items.length - 1} title={T._('Next', 'Admin')}>
                            <Icon>
                                <AngleRightIcon />
                            </Icon>
                        </NavArrow>
                    </>
                )}

                <FooterBar>
                    <ModelName>{item.label}</ModelName>
                </FooterBar>
            </DialogBox>
        </Backdrop>
    );
};

export default Local3DPreviewDialog;
